import { useState, useRef, useCallback, useMemo, useEffect } from 'react'
import { useParams, useSearchParams, Link as RouterLink } from 'react-router-dom'
import {
  Box, Typography, Alert, Breadcrumbs, Link, Button, Drawer, IconButton, CircularProgress,
} from '@mui/material'
import { NavigateNext, Close, BarChartRounded, LayersOutlined, Notes } from '@mui/icons-material'
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community'
import { AgGridReact } from 'ag-grid-react'

ModuleRegistry.registerModules([AllCommunityModule])

// FAR-75 §2: neutral pill buttons -- outlined, no selected or active styling.
const pillSx = {
  borderRadius: 999,
  textTransform: 'none',
  fontWeight: 600,
  borderColor: 'divider',
  color: 'text.primary',
  px: 2,
  '&:hover': { borderColor: 'text.secondary', backgroundColor: 'action.hover' },
}

// FAR-88 S2 / FAR-90 S2: a header pill -- leading icon, the label in muted
// regular weight ("Base Plan:"), then the value in bold.
function HeaderPill({ icon, label, value, onClick, testId }) {
  return (
    <Button
      variant="outlined"
      color="inherit"
      onClick={onClick}
      startIcon={icon}
      data-testid={testId}
      sx={{
        ...pillSx, height: 40, px: 2, gap: 0.25,
        '& .MuiButton-startIcon': { mr: 0.75, color: 'text.secondary' },
        '& .MuiButton-startIcon > *:nth-of-type(1)': { fontSize: 18 },
      }}
    >
      <Box component="span" sx={{ fontWeight: 400, color: 'text.secondary', mr: 0.75 }}>{label}</Box>
      <Box component="span" sx={{ fontWeight: 700 }}>{value}</Box>
    </Button>
  )
}

import { getColumnDefs, defaultColDef, gridOptions } from '../components/planningboard/GridConfig'
import { boardMonths, toGridRow, hoursFor } from '../components/planningboard/boardRows'
import { DOSCellRenderer, CasesCellRenderer, IssueCellRenderer, DescriptionCellRenderer } from '../components/planningboard/CellRenderers'
import FilterBar from '../components/planningboard/FilterBar'
import SearchBar from '../components/planningboard/SearchBar'
import ModificationsPanel from '../components/planningboard/ModificationsPanel'
import BoardTabs from '../components/planningboard/BoardTabs'
import BasePlanModal from '../components/planningboard/BasePlanModal'
import CapacityRiskCard from '../components/planningboard/CapacityRiskCard'
import { horizonMonths, cellModFor, nextCellValues, CELL } from '../components/planningboard/fourToTwelve'
import NextThreeMonthsCard from '../components/planningboard/NextThreeMonthsCard'
import { useSyncedScrollX } from '../components/planningboard/useSyncedScrollX'
import { nextThreeMonths, tabLabels } from '../components/planningboard/nextThreeMonths'
import { boardIsCurrent, boardCaughtUp, reviewItems, backToBasePlan } from '../components/planningboard/basePlanSwitch'
import { withoutMonth } from '../components/planningboard/modificationsModel'
import {
  resolveTab, basePlanPill, modificationPill, boardSubtitle, releasePill,
} from '../components/planningboard/boardShell'
import ContextMenu from '../components/planningboard/ContextMenu'
import OverrideBottomSheet from '../components/planningboard/OverrideBottomSheet'
import ApprovalControl, { APPROVED } from '../components/planningboard/ApprovalControl'
import LoadingState from '../components/common/LoadingState'
import { useApi } from '../hooks/useApi'
import { api } from '../services/api'
import { usePlantContext } from '../hooks/usePlantContext'
import { useBasePlans } from '../hooks/useBasePlans'
import { applyModifications } from '../utils/applyModifications'

// ─── fallback demo data — used when API is unavailable or returns empty ──────


// ─── Component ───────────────────────────────────────────────────────────────

export default function PlanningBoard() {
  const gridRef = useRef(null)
  const { planId } = useParams()
  const { loading, error, get, put } = useApi()
  const { plant, planContext, scenarioContext } = usePlantContext()


  // FAR-72: real rows only. This used to start as (and fall back to) DEMO_ROWS,
  // so an empty or failed load looked like a full board.
  const [baseRowData, setBaseRowData] = useState([])
  const [months, setMonths] = useState([])
  const [loadError, setLoadError] = useState(null)
  // Modifications auto-save (FAR-75 §2, FAR-78 §2): every change is written
  // immediately; there's no draft, no Save button and nothing to warn about
  // on leaving. The grid previews `modifications` straight away, and the board
  // reloads once the server has recalculated.
  const [modifications, setModifications] = useState([])
  const [modsLoading, setModsLoading] = useState(false)
  const [saveError, setSaveError] = useState(null)
  const [boardVersion, setBoardVersion] = useState(0) // bump to reload the board
  // FAR-75: the Modified/Base Plan toggle is gone; the board always shows
  // the plan as modified. The Base Plan is seen through its modal (and, on
  // the 4–18 Month tab, FAR-76's Δ vs Base Plan row).
  const displayMode = 'modified'
  // FAR-75 §3: the tab is in the URL so it survives a reload and can be
  // linked; a locked or unknown tab falls back to 4–18 Month.
  const [searchParams, setSearchParams] = useSearchParams()
  const tab = resolveTab(searchParams.get('tab'))
  const setTab = (next) => {
    const sp = new URLSearchParams(searchParams)
    sp.set('tab', next)
    setSearchParams(sp, { replace: true })
  }
  const [modsOpen, setModsOpen] = useState(false)
  const [basePlanOpen, setBasePlanOpen] = useState(false)
  const [folderName, setFolderName] = useState('')
  // FAR-76: the 4–18 Month tab.
  const [capacity, setCapacity] = useState(null)
  // FAR-94: H per line and month (line → month → hours), served by the API.
  const [grossHours, setGrossHours] = useState({})
  const [baseRowsByItem, setBaseRowsByItem] = useState(null)
  const [skuLine, setSkuLine] = useState('')
  const [selectedSkus, setSelectedSkus] = useState(() => new Set())
  const [initialLoad, setInitialLoad] = useState(true)
  const [planName, setPlanName] = useState('')
  const [plan, setPlan] = useState(null) // FAR-61: status + approval stamp for the header
  // FAR-74: name the Base Plan (and Modification List) this plan was built on.
  const [basePlansVersion, setBasePlansVersion] = useState(0)
  const basePlans = useBasePlans(plant?.plantCode, basePlansVersion)
  // FAR-79 §5: while a switch recalculates, the board waits rather than show
  // a mix of old and new numbers.
  const [switching, setSwitching] = useState(false)
  const [switchError, setSwitchError] = useState(null)
  const [modListName, setModListName] = useState('')
  // FAR-56: a save already knows the new list's name, so the FAR-74 effect
  // must not re-fetch it -- it would race the save and could overwrite it.
  const knownListNames = useRef({})
  const [warehouseNames, setWarehouseNames] = useState({})

  const rowData = useMemo(
    () => applyModifications(baseRowData, modifications, displayMode, months),
    [baseRowData, modifications, displayMode, months],
  )
  const [filters, setFilters] = useState({
    line: '',
    briteCode: '',
    issueStatus: '',
  })
  const [adjustments, setAdjustments] = useState([])
  const [contextMenu, setContextMenu] = useState({ open: false, position: null, rowData: null })
  const [overrideSheet, setOverrideSheet] = useState({ open: false, editContext: null })

  const activePlanId = planId || planContext?.id

  useEffect(() => {
    let cancelled = false
    const query = new URLSearchParams({ plant: plant.plantCode })
    if (activePlanId) query.set('planId', activePlanId)
    get(`/board?${query}`)
      .then((data) => {
        if (cancelled) return
        const engineRows = Array.isArray(data) ? data : data?.rows || []
        setMonths(boardMonths(engineRows))
        setBaseRowData(engineRows.map(toGridRow))
        setLoadError(null)
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err?.message || 'Could not load the planning board')
      })
      .finally(() => {
        if (!cancelled) setInitialLoad(false)
      })
    return () => { cancelled = true }
  }, [plant.plantCode, activePlanId, boardVersion]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!activePlanId) return
    let cancelled = false
    get(`/plans/${activePlanId}?plant=${plant.plantCode}`)
      .then((data) => {
        if (cancelled || !data?.name) return
        setPlanName(data.name)
        setPlan(data)
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [activePlanId, plant.plantCode]) // eslint-disable-line react-hooks/exhaustive-deps

  // FAR-75 §2: the subtitle names the plan's folder. GET /folders is one
  // small read; a plan with no folder (or a lookup failure) just shows the
  // plan name.
  useEffect(() => {
    const folderId = plan?.folderId
    if (!folderId) {
      setFolderName('')
      return undefined
    }
    let cancelled = false
    get(`/folders?plant=${encodeURIComponent(plant.plantCode)}`)
      .then((list) => {
        if (cancelled) return
        const f = (Array.isArray(list) ? list : []).find((x) => x.id === folderId)
        setFolderName(f?.name || '')
      })
      .catch(() => { if (!cancelled) setFolderName('') })
    return () => { cancelled = true }
  }, [plan?.folderId, plant.plantCode]) // eslint-disable-line react-hooks/exhaustive-deps

  // FAR-76: months 4–18 of the plan, and what the tab needs for them.
  const horizon = useMemo(() => horizonMonths(months), [months])
  const horizonKey = horizon.map((m) => m.iso).join(',')
  // FAR-77 §5.5 / FAR-83 §4.3: SKU-months whose demand moved since the
  // plan's Base Plan, shown in blue on both tabs.
  const [forecastChanges, setForecastChanges] = useState(null)
  useEffect(() => {
    if (!plan?.basePlanId) { setForecastChanges(null); return undefined }
    let cancelled = false
    get(`/base-plans/${encodeURIComponent(plan.basePlanId)}/forecast-changes?plant=${encodeURIComponent(plant.plantCode)}`)
      .then((d) => { if (!cancelled) setForecastChanges(d?.changes || null) })
      .catch(() => { if (!cancelled) setForecastChanges(null) })
    return () => { cancelled = true }
  }, [plan?.basePlanId, plant.plantCode]) // eslint-disable-line react-hooks/exhaustive-deps

  // FAR-83 #13: Capacity and Risk and the SKU Plan scroll sideways together,
  // in whichever tab is shown. A tab switch unmounts both, so both start at 0.
  const scrollX = useSyncedScrollX()

  // FAR-77: the plan's first three months, and their line capacity.
  const near = useMemo(() => nextThreeMonths(months), [months])
  const nearKey = near.map((m) => m.iso).join(',')
  const [nearCapacity, setNearCapacity] = useState(null)
  useEffect(() => {
    if (!nearKey) return undefined
    let cancelled = false
    const bp = plan?.basePlanId ? `&basePlanId=${encodeURIComponent(plan.basePlanId)}` : ''
    get(`/capacity/lines?plant=${encodeURIComponent(plant.plantCode)}&months=${encodeURIComponent(nearKey)}${bp}`)
      .then((d) => { if (!cancelled) setNearCapacity(d || null) })
      .catch(() => { if (!cancelled) setNearCapacity(null) })
    return () => { cancelled = true }
  }, [plant.plantCode, nearKey, plan?.basePlanId]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!horizonKey) return undefined
    let cancelled = false
    // FAR-77 §1: with the plan's Base Plan, a partial first month's capacity
    // is its remaining working days.
    const bp = plan?.basePlanId ? `&basePlanId=${encodeURIComponent(plan.basePlanId)}` : ''
    get(`/capacity/lines?plant=${encodeURIComponent(plant.plantCode)}&months=${encodeURIComponent(horizonKey)}${bp}`)
      .then((d) => {
        if (cancelled || !d) return
        setCapacity(d.config || null)
        setGrossHours(d.grossHours || {})
      })
      .catch(() => { if (!cancelled) setCapacity(null) })
    return () => { cancelled = true }
  }, [plant.plantCode, horizonKey, plan?.basePlanId]) // eslint-disable-line react-hooks/exhaustive-deps

  // "Δ vs Base Plan" compares with the plan's own Base Plan, row by row.
  useEffect(() => {
    const id = plan?.basePlanId
    if (!id) {
      setBaseRowsByItem(null)
      return undefined
    }
    let cancelled = false
    get(`/base-plans/${encodeURIComponent(id)}/rows?plant=${encodeURIComponent(plant.plantCode)}`)
      .then((rows) => {
        if (cancelled) return
        const byItem = {}
        for (const r of Array.isArray(rows) ? rows : []) {
          const g = toGridRow(r)
          byItem[g.itemNumber] = g
        }
        setBaseRowsByItem(byItem)
      })
      .catch(() => { if (!cancelled) setBaseRowsByItem(null) })
    return () => { cancelled = true }
  }, [plan?.basePlanId, plant.plantCode]) // eslint-disable-line react-hooks/exhaustive-deps

  const selectSkus = useCallback((ids, on) => {
    setSelectedSkus((prev) => {
      const next = new Set(prev)
      for (const id of ids) {
        if (on) next.add(id)
        else next.delete(id)
      }
      return next
    })
  }, [])

  // Load the plan's modifications. The plant is required (FAR-62): the
  // server loads the plan by plant, so another plant's plan isn't found.
  useEffect(() => {
    if (!activePlanId) return undefined
    let cancelled = false
    setModsLoading(true)
    get(`/modifications?planId=${encodeURIComponent(activePlanId)}&plant=${encodeURIComponent(plant.plantCode)}`)
      .then((data) => { if (!cancelled) setModifications(Array.isArray(data) ? data : []) })
      .catch(() => {})
      .finally(() => { if (!cancelled) setModsLoading(false) })
    return () => { cancelled = true }
  }, [activePlanId, plant.plantCode]) // eslint-disable-line react-hooks/exhaustive-deps

  // The server recalculates asynchronously (doscalc), so reload the board a
  // moment after a change lands. The grid already previews it in the meantime.
  const reloadSoon = useCallback(() => {
    setTimeout(() => setBoardVersion((v) => v + 1), 1500)
  }, [])

  // autoSave applies a change locally at once, writes it, and on failure puts
  // the list back and says so -- the board never shows a change that wasn't
  // saved.
  const autoSave = useCallback(async (optimistic, write) => {
    const before = modifications
    setSaveError(null)
    if (optimistic) setModifications(optimistic(before))
    try {
      const result = await write()
      reloadSoon()
      return result
    } catch (err) {
      setModifications(before)
      setSaveError(err?.message || 'Could not save that change')
      return false // callers (e.g. Remove's Undo) need to tell failure apart
    }
  }, [modifications, reloadSoon])

  const ids = { planId: activePlanId, plant: plant.plantCode }

  const addModification = useCallback((mod) => autoSave(null, async () => {
    const created = await api.post('/modifications', { ...mod, ...ids })
    if (created) setModifications((list) => [created, ...list.map((m) => ({ ...m, listPosition: (m.listPosition || 0) + 1 }))])
    return created
  }), [autoSave, activePlanId, plant.plantCode]) // eslint-disable-line react-hooks/exhaustive-deps

  const removeModification = useCallback((id) => autoSave(
    (list) => list.filter((m) => m.id !== id),
    () => api.delete(`/modifications/${encodeURIComponent(id)}?planId=${encodeURIComponent(activePlanId)}&plant=${encodeURIComponent(plant.plantCode)}`),
  ), [autoSave, activePlanId, plant.plantCode])

  const toggleIgnore = useCallback((id) => autoSave(
    (list) => list.map((m) => (m.id === id ? { ...m, isIgnored: !m.isIgnored } : m)),
    () => api.post(`/modifications/${encodeURIComponent(id)}/ignore`, ids),
  ), [autoSave, activePlanId, plant.plantCode]) // eslint-disable-line react-hooks/exhaustive-deps

  // FAR-76 §4: typing a SKU-month's Units. All of a SKU's edits are one Cell
  // modification; typing the Base Plan value back removes that month, and
  // the modification goes when no months remain. Auto-saved like the rest.
  const editUnits = useCallback((row, month, units, { remove = false } = {}) => {
    const existing = cellModFor(modifications, row.itemNumber)
    const baseUnits = baseRowsByItem?.[row.itemNumber]?.[`${month.key}_cases`]
    const monthValues = remove
      ? withoutMonth(existing || {}, month.iso)
      : nextCellValues(existing, month.iso, units, baseUnits)
    if (!existing) {
      if (Object.keys(monthValues).length === 0) return undefined
      return addModification({
        name: `Units: ${row.itemNumber}`, scope: 'ItemNumber', identifier: row.itemNumber,
        changeType: CELL, monthValues,
      })
    }
    if (Object.keys(monthValues).length === 0) return removeModification(existing.id)
    return autoSave(
      (list) => list.map((m) => (m.id === existing.id ? { ...m, monthValues } : m)),
      () => api.put(`/modifications/${encodeURIComponent(existing.id)}`, {
        ...ids, name: existing.name, scope: existing.scope, identifier: existing.identifier,
        changeType: CELL, monthValues,
      }),
    )
  }, [modifications, baseRowsByItem, addModification, removeModification, autoSave, activePlanId, plant.plantCode]) // eslint-disable-line react-hooks/exhaustive-deps

  // The "n edits" pill: revert the SKU to the Base Plan.
  const revertSku = useCallback((itemNumber) => {
    const existing = cellModFor(modifications, itemNumber)
    return existing ? removeModification(existing.id) : undefined
  }, [modifications, removeModification])

  const editCounts = useMemo(() => {
    const counts = {}
    for (const m of modifications) {
      if (m.changeType === CELL && !m.isIgnored) counts[m.identifier] = Object.keys(m.monthValues || {}).length
    }
    return counts
  }, [modifications])

  // FAR-78 §5: Undo a removed rule. Recreated, then put back where it was --
  // its position decides which rule wins. `before` is the list without it.
  const restoreModification = useCallback(async (mod) => {
    const before = modifications
    const { id: _id, createdAt: _c, createdBy: _b, listPosition, ...body } = mod
    const created = await api.post('/modifications', { ...body, ...ids }).catch((err) => {
      setSaveError(err?.message || 'Could not undo that'); return null
    })
    if (!created) return false // the panel says the rule is still removed
    const ordered = [...before]
    ordered.splice(Math.max(0, Math.min(ordered.length, (listPosition || 1) - 1)), 0, created)
    const renumbered = ordered.map((m, i) => ({ ...m, listPosition: i + 1 }))
    return autoSave(
      () => renumbered,
      () => api.post('/modifications/reorder', { ...ids, ordering: renumbered.map((m) => ({ id: m.id, listPosition: m.listPosition })) }),
    )
  }, [modifications, autoSave, activePlanId, plant.plantCode]) // eslint-disable-line react-hooks/exhaustive-deps

  // FAR-78 §6: revert one typed month, or every typed Units edit.
  const revertCell = useCallback((cell, iso) => (
    editUnits({ itemNumber: cell.identifier }, { iso, key: months.find((m) => m.iso === iso)?.key }, null, { remove: true })
  ), [months, editUnits])

  const revertAll = useCallback(async () => {
    for (const c of modifications.filter((m) => m.changeType === CELL)) {
      // eslint-disable-next-line no-await-in-loop
      if (await removeModification(c.id) === false) return
    }
  }, [modifications, removeModification])

  // FAR-79 §5: switch the plan's Base Plan, then hold the board until the
  // recalculation has rebuilt it from the new one.
  const switchBasePlan = useCallback(async (basePlanId) => {
    if (!activePlanId || !plant?.plantCode) return
    setSwitchError(null)
    let updated
    try {
      updated = await api.patch(`/plans/${encodeURIComponent(activePlanId)}/base-plan`, { plant: plant.plantCode, basePlanId })
    } catch (err) {
      setSwitchError(err?.message || 'Could not switch the Base Plan')
      return
    }
    setBasePlanOpen(false)
    if (updated) setPlan(updated)
    setSwitching(true)
  }, [activePlanId, plant.plantCode])

  // Poll the plan until its board is on the new Base Plan (doscalc writes
  // boardBasePlanId), then reload the board, its Base Plan rows and the list.
  useEffect(() => {
    if (!switching || !activePlanId) return undefined
    let cancelled = false
    let tries = 0
    let timer = null // the one pending poll, so cleanup always clears it
    const tick = async () => {
      if (cancelled) return
      tries += 1
      try {
        const p = await get(`/plans/${activePlanId}?plant=${plant.plantCode}`)
        if (cancelled) return
        if (p && boardCaughtUp(p)) { // strict: see basePlanSwitch.js
          setPlan(p)
          setSwitching(false)
          setBoardVersion((v) => v + 1)
          setBasePlansVersion((v) => v + 1)
          return
        }
      } catch { /* keep waiting */ }
      if (tries >= 60) { // ~2 minutes
        setSwitching(false)
        setLoadError('The plan is still recalculating on the new Base Plan. Reload in a moment.')
        return
      }
      timer = setTimeout(tick, 2000)
    }
    timer = setTimeout(tick, 1500)
    return () => { cancelled = true; clearTimeout(timer) }
  }, [switching, activePlanId, plant.plantCode]) // eslint-disable-line react-hooks/exhaustive-deps

  // A plan opened mid-switch (or reloaded) waits too.
  useEffect(() => {
    if (plan && !boardIsCurrent(plan)) setSwitching(true)
  }, [plan?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  // FAR-76 §4.4 / FAR-78 §3: the flagged typed edits, and the one action that
  // takes exactly those back to the Base Plan.
  const review = useMemo(() => reviewItems(rowData, months, modifications), [rowData, months, modifications])
  const resolveReview = useCallback(async () => {
    for (const { mod, monthValues } of backToBasePlan(review, modifications)) {
      const result = Object.keys(monthValues).length === 0
        // eslint-disable-next-line no-await-in-loop
        ? await removeModification(mod.id)
        // eslint-disable-next-line no-await-in-loop
        : await autoSave(
          (list) => list.map((m) => (m.id === mod.id ? { ...m, monthValues } : m)),
          () => api.put(`/modifications/${encodeURIComponent(mod.id)}`, {
            ...ids, name: mod.name, scope: mod.scope, identifier: mod.identifier, changeType: CELL, monthValues,
          }),
        )
      if (result === false) return
    }
  }, [review, modifications, removeModification, autoSave, activePlanId, plant.plantCode]) // eslint-disable-line react-hooks/exhaustive-deps

  // FAR-77 §5.6: the bulk bar saves one rule per Apply.
  const applyRule = useCallback(async (rule) => {
    const created = await addModification(rule)
    return created ? created : false
  }, [addModification])


  // FAR-67: plant/warehouse names for the demand-source tooltip.
  useEffect(() => {
    let cancelled = false
    get('/plants/reference')
      .then((refs) => {
        if (cancelled) return
        const names = {}
        for (const r of refs?.locations || []) names[r.code] = r.name
        setWarehouseNames(names)
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // FAR-74: the plan's Modification List name, for the header.
  useEffect(() => {
    const listID = plan?.modificationListId
    if (!listID) {
      setModListName('')
      return undefined
    }
    if (listID in knownListNames.current) {
      setModListName(knownListNames.current[listID])
      return undefined
    }
    let cancelled = false
    get(`/modification-lists/${listID}?plant=${plant.plantCode}`)
      .then((data) => { if (!cancelled) setModListName(data?.name || '') })
      .catch(() => { if (!cancelled) setModListName('') })
    return () => { cancelled = true }
  }, [plan?.modificationListId, plant.plantCode]) // eslint-disable-line react-hooks/exhaustive-deps

  const lineOptions = useMemo(
    () => [...new Set(rowData.map((r) => r.line).filter(Boolean))].sort(),
    [rowData],
  )

  const briteCodeOptions = useMemo(
    () => [...new Set(rowData.map((r) => r.briteCode))].sort(),
    [rowData],
  )

  const columnDefs = useMemo(() => getColumnDefs(months), [months])
  // FAR-67: the cell renderer needs the plant being planned and the warehouse
  // names for its tooltip; a renderer gets no props, so they travel as context.
  const gridContext = useMemo(
    () => ({ makingPlant: plant?.plantCode, warehouseNames }),
    [plant?.plantCode, warehouseNames],
  )

  const components = useMemo(
    () => ({
      dosCellRenderer: DOSCellRenderer,
      casesCellRenderer: CasesCellRenderer,
      issueCellRenderer: IssueCellRenderer,
      descriptionCellRenderer: DescriptionCellRenderer,
    }),
    [],
  )

  // ─── External filter ────────────────────────────────────────────────────

  const isExternalFilterPresent = useCallback(
    () => Object.values(filters).some((v) => v !== ''),
    [filters],
  )

  const doesExternalFilterPass = useCallback(
    (node) => {
      const d = node.data
      if (filters.line && String(d.line) !== filters.line) return false
      if (filters.briteCode && d.briteCode !== filters.briteCode) return false
      if (filters.issueStatus) {
        if (filters.issueStatus === 'issues' && !d.hasIssue) return false
        if (filters.issueStatus === 'clear' && d.hasIssue) return false
      }
      return true
    },
    [filters],
  )

  useEffect(() => {
    gridRef.current?.api?.onFilterChanged()
  }, [filters])

  // ─── Cell editing → scenario adjustments ────────────────────────────────

  const onCellValueChanged = useCallback(
    (params) => {
      if (!params.colDef.field.endsWith('_cases')) return
      const monthKey = params.colDef.field.replace('_cases', '')
      const month = months.find((m) => m.key === monthKey)
      const monthLabel = month?.label || monthKey

      const adj = {
        itemNumber: params.data.itemNumber,
        description: params.data.description,
        month: monthLabel,
        field: 'CASES',
        oldValue: params.oldValue,
        newValue: params.newValue,
      }

      setAdjustments((prev) => [...prev, adj])

      // FAR-72: the item's real run rate from the engine. This used made-up
      // defaults (cpm 400, efficiency 0.85, pack 12) the rows never carried.
      params.node.setDataValue(`${monthKey}_hrs`, hoursFor(params.newValue, params.data.runRateCasesPerHour))
      params.node.setDataValue(`${monthKey}_scenarioModified`, true)

      put('/board/cell', {
        materialNumber: params.data.itemNumber,
        // The backend matches months as "2026-09"; it was sent "Sep 2026" (a
        // label), so every save failed with "Month not found".
        month: month?.iso || monthKey,
        scheduledCases: params.newValue,
        rationale: adj.description,
        planVersionId: activePlanId,
        scenarioId: scenarioContext?.id,
      }).catch(() => {
        // persist failed silently — local state is still updated
      })

      if (scenarioContext) {
        setOverrideSheet({
          open: true,
          editContext: {
            ...adj,
            monthLabel,
          },
        })
      }
    },
    [scenarioContext, activePlanId, put, months],
  )

  // ─── Context menu ──────────────────────────────────────────────────────

  const onCellContextMenu = useCallback((params) => {
    params.event.preventDefault()
    setContextMenu({
      open: true,
      position: { top: params.event.clientY, left: params.event.clientX },
      rowData: params.data,
    })
  }, [])

  const handleContextAction = useCallback((action, row) => {
    // Placeholder for action handlers
    console.info(`Action: ${action}`, row)
  }, [])

  const handleRemoveAdjustment = useCallback((index) => {
    setAdjustments((prev) => prev.filter((_, i) => i !== index))
  }, [])

  const handleOverrideSave = useCallback((overrideData) => {
    console.info('Override saved:', overrideData)
  }, [])

  // ─── Render ────────────────────────────────────────────────────────────

  if (initialLoad && loading) return <LoadingState message="Loading planning board..." />

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 1.5 }}>
      {/* Breadcrumb */}
      <Breadcrumbs separator={<NavigateNext sx={{ fontSize: 16 }} />} sx={{ fontSize: '0.85rem' }}>
        <Link component={RouterLink} to="/plans" underline="hover" color="text.secondary">
          Plans
        </Link>
        <Typography color="text.primary" sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
          {planName || 'Planning Board'}
        </Typography>
      </Breadcrumbs>

      {/* FAR-75 §2: header -- title, Folder / Plan subtitle, and two neutral
          pills at the top right, neither highlighted. */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flexWrap: 'wrap' }}>
        {/* FAR-88 S1: green bar-chart icon, heading-font title, muted subtitle. */}
        <Box sx={{ flex: 1, minWidth: 240, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <BarChartRounded aria-hidden sx={{ fontSize: 36, color: 'primary.main' }} data-testid="board-title-icon" />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h3">Planning Board</Typography>
            <Typography variant="body2" color="text.secondary" data-testid="board-subtitle">
              {boardSubtitle(folderName, planName) || (activePlanId ? '' : 'No plan selected')}
            </Typography>
          </Box>
        </Box>
        {activePlanId && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            {/* FAR-88 S2: neutral outlined pills, leading icon, muted label, bold value. */}
            <HeaderPill
              icon={<LayersOutlined />}
              {...basePlanPill(basePlans[plan?.basePlanId], !!plan?.basePlanId)}
              onClick={() => setBasePlanOpen(true)}
              testId="base-plan-pill"
            />
            <HeaderPill
              icon={<Notes />}
              {...modificationPill(modListName, !!plan?.modificationListId)}
              onClick={() => setModsOpen(true)}
              testId="modification-pill"
            />
            {/* Not in FAR-75, kept from FAR-61: approval lives on the board. */}
            {/* FAR-88 A1: approval follows the active tab. */}
            <ApprovalControl plan={plan} plantCode={plant.plantCode} onChange={setPlan}
              tab={tab} tabLabel={tabLabels(months)[tab]} tabMonths={tab === '3month' ? near : horizon}
              basePlan={basePlans[plan?.basePlanId]} modificationName={modListName} />
          </Box>
        )}
      </Box>

      {/* FAR-75 §3: one tab per planning horizon. Their contents come with
          FAR-76 (4–18 Month) and FAR-77 (3 Month); until then both show the
          current grid, so nothing that works today goes away. */}
      <BoardTabs value={tab} onChange={setTab} labels={tabLabels(months)} release={releasePill(near)} />

      {loadError && (
        <Alert severity="error" sx={{ py: 0.5 }}>
          Couldn't load the planning board: {loadError}
        </Alert>
      )}
      {!loadError && !initialLoad && baseRowData.length === 0 && (
        <Alert severity="info" sx={{ py: 0.5 }}>
          {activePlanId
            ? 'This plan has no planning rows yet. Rows appear once the plan has been calculated.'
            : 'No plan selected. Open a plan from Plans to see its planning board.'}
        </Alert>
      )}

      {/* FAR-75 §2: the Modification pill opens the Modifications panel on the
          right. Every action in it saves immediately (FAR-78 §2). */}
      <Drawer anchor="right" open={modsOpen} onClose={() => setModsOpen(false)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 520 }, p: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6" sx={{ flex: 1 }}>Modifications</Typography>
          <IconButton aria-label="Close modifications" onClick={() => setModsOpen(false)}>
            <Close />
          </IconButton>
        </Box>
        <ModificationsPanel
          months={months}
          modifications={modifications}
          loading={modsLoading}
          saveError={saveError}
          listName={modListName}
          rows={rowData}
          baseRowsByItem={baseRowsByItem}
          onRemove={removeModification}
          onRestore={restoreModification}
          onToggleIgnore={toggleIgnore}
          onRevertCell={revertCell}
          onRevertSku={revertSku}
          onRevertAll={revertAll}
          readOnly={plan?.status === APPROVED}
          review={review}
          onBackToBasePlan={resolveReview}
        />
      </Drawer>

      <BasePlanModal
        open={basePlanOpen}
        onClose={() => { setBasePlanOpen(false); setSwitchError(null) }}
        basePlans={Object.values(basePlans)}
        currentId={plan?.basePlanId}
        planName={planName}
        modificationName={modListName}
        onSwitch={switchBasePlan}
        readOnly={plan?.status === APPROVED}
        error={switchError}
      />

      {/* FAR-79 §5: never a mix -- hold the board until it's on the new Base Plan. */}
      {switching && (
        <Alert severity="info" icon={<CircularProgress size={18} />} sx={{ mb: 2 }} data-testid="switching-banner">
          Applying Modification to new Base Plan…
        </Alert>
      )}

      {/* FAR-83 §4.4: the review banner is gone; each tab's SKU Plan header shows
          the ⚠ icon and Back to Base Plan instead. */}

      {/* FAR-83: Months 4–18 -- the Next 3 Months layout without line features:
          one Overall Capacity row, no Line / Rate/hr / Line filter. */}
      {tab === '4-12month' && baseRowData.length > 0 && !switching && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }} data-testid="tab-4-12">
          <CapacityRiskCard
            rows={rowData}
            months={horizon}
            capacity={capacity}
            grossHours={grossHours}
            basePlan={basePlans[plan?.basePlanId]}
            overall
            scrollRef={scrollX.capacityRef}
          />
          <NextThreeMonthsCard
            testId="m412"
            withLines={false}
            rows={rowData}
            months={horizon}
            basePlan={basePlans[plan?.basePlanId]}
            line=""
            onLineChange={() => {}}
            lines={[]}
            baseRowsByItem={baseRowsByItem}
            modifications={modifications}
            selected={selectedSkus}
            onSelect={selectSkus}
            editCounts={editCounts}
            onEditUnits={editUnits}
            onRevertSku={revertSku}
            onApplyRule={applyRule}
            review={review}
            onBackToBasePlan={resolveReview}
            readOnly={plan?.status === APPROVED}
            forecastChanges={forecastChanges}
            modificationName={modListName}
            scrollRef={scrollX.gridRef}
          />
        </Box>
      )}

      {/* FAR-77: Next 3 Months -- Capacity and Risk, then the SKU Plan. The old
          AG Grid board is no longer rendered; its handlers are removed in a
          follow-up. */}
      {tab === '3month' && baseRowData.length > 0 && !switching && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }} data-testid="tab-3">
          <CapacityRiskCard
            rows={rowData}
            months={near}
            capacity={nearCapacity?.config || capacity}
            grossHours={nearCapacity?.grossHours || {}}
            basePlan={basePlans[plan?.basePlanId]}
            activeLine={skuLine}
            onLineClick={setSkuLine}
            scrollRef={scrollX.capacityRef}
          />
          <NextThreeMonthsCard
            rows={rowData}
            months={near}
            basePlan={basePlans[plan?.basePlanId]}
            line={skuLine}
            onLineChange={setSkuLine}
            lines={lineOptions}
            baseRowsByItem={baseRowsByItem}
            modifications={modifications}
            selected={selectedSkus}
            onSelect={selectSkus}
            editCounts={editCounts}
            onEditUnits={editUnits}
            onRevertSku={revertSku}
            onApplyRule={applyRule}
            review={review}
            onBackToBasePlan={resolveReview}
            readOnly={plan?.status === APPROVED}
            forecastChanges={forecastChanges}
            modificationName={modListName}
            scrollRef={scrollX.gridRef}
          />
        </Box>
      )}

      {/* Context menu */}
      <ContextMenu
        open={contextMenu.open}
        anchorPosition={contextMenu.position}
        onClose={() => setContextMenu({ open: false, position: null, rowData: null })}
        onAction={handleContextAction}
        rowData={contextMenu.rowData}
      />

      {/* Override bottom sheet */}
      <OverrideBottomSheet
        open={overrideSheet.open}
        onClose={() => setOverrideSheet({ open: false, editContext: null })}
        onSave={handleOverrideSave}
        editContext={overrideSheet.editContext}
      />
    </Box>
  )
}
